/**
 * useTestResultInsights
 *
 * Reads the patient's test results and produces a `ResultInterpretation`
 * per recognized biomarker. This hook is deliberately narrow after
 * issue #1593: overview cards need only value + unit + a category
 * (for the range-bar marker). All clinical framing (thresholds, source
 * citations, disclaimer) lives in the tapped detail sheet, not here.
 *
 * The category comes from the one rule every result surface shares,
 * `resolveResultCategory` in `src/packages/test-results/result-category.ts`:
 * the lab's own assessment, otherwise the client thresholds (taken from ASRM,
 * ESHRE and WHO guidance and cited there), otherwise no category. A
 * cycle-dependent result that neither the lab nor the thresholds can place
 * gets no card here (issue #1593 point 4: no invented day-3 default).
 */

import { useMemo } from "react";
import {
  useTestResults,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { useTranslations } from "@src/packages/i18n";
import type { ResultInterpretationContext } from "@src/packages/test-results/interpretation-context";
import {
  categorizableFromTestResult,
  getInterpretedTest,
  resolveSemenAnalysisCategory,
  type InterpretedTest,
  type ResultCategory,
  type ResultCategorySource,
} from "@src/packages/test-results/result-category";
import { useResultCategoryResolver } from "@src/packages/test-results/hooks/useResultCategory";

// Semen-fluid analysis rows carry concentration / totalMotility /
// morphology / volume fields on the wire that aren't part of the base
// TestResult type because they're SA-specific. We extend locally and
// read defensively rather than casting through `any` / `unknown`.
type SemenAnalysisRow = TestResult & {
  concentration?: string | number | null;
  count?: string | number | null;
  totalMotility?: string | number | null;
  motility?: string | number | null;
  morphology?: string | number | null;
  volume?: string | number | null;
};

export type { InterpretedTest, ResultCategory };

/** Copy keys and the fallback unit for each single-value test. */
const INTERPRETED_TEST_COPY: Record<
  Exclude<InterpretedTest, "semen_analysis">,
  { testNameKey: string; testPurposeKey: string; defaultUnit: string }
> = {
  amh: {
    testNameKey: "test_result_insights.amh.test_name",
    testPurposeKey: "test_result_insights.amh.test_purpose",
    defaultUnit: "ng/mL",
  },
  fsh: {
    testNameKey: "test_result_insights.fsh.test_name",
    testPurposeKey: "test_result_insights.fsh.test_purpose",
    defaultUnit: "mIU/mL",
  },
  progesterone: {
    testNameKey: "test_result_insights.progesterone.test_name",
    testPurposeKey: "test_result_insights.progesterone.test_purpose",
    defaultUnit: "ng/mL",
  },
  e2: {
    testNameKey: "test_result_insights.e2.test_name",
    testPurposeKey: "test_result_insights.e2.test_purpose",
    defaultUnit: "pg/mL",
  },
  lh: {
    testNameKey: "test_result_insights.lh.test_name",
    testPurposeKey: "test_result_insights.lh.test_purpose",
    defaultUnit: "mIU/mL",
  },
};

export type ResultInterpretation = {
  testName: string;
  /**
   * Which test this is, untranslated. `testName` is copy and reads differently
   * in every language, so a caller asking "is there a semen analysis?" asks
   * this. Absent on interpretations built outside this hook.
   */
  test?: InterpretedTest;
  /** One-liner describing what the test measures (not interpreting the
   *  user's value). Same string regardless of category. Used as the
   *  subtitle on the home result card. */
  testPurpose: string;
  value: string;
  /**
   * True when the result has no single value to print (a semen analysis
   * measures several): the card shows the bar without a number and the
   * values live in the detail view. An explicit flag, because `value`
   * is translated copy and can't be compared against a fixed string.
   */
  valueInDetails?: boolean;
  unit: string;
  /** Null when neither the lab nor the client thresholds can place it. */
  category: ResultCategory | null;
  /** Where the category came from. "api" = the lab's own assessment
   *  (the source of truth when available). "client" = derived on the
   *  frontend from published-guideline thresholds. */
  categorySource?: ResultCategorySource;
  /** Source `TestResult.id`, which lets callers look up the raw row when
   *  they need to open a detail sheet (which needs id/date/history).
   *  See `TestResultInsightCardWithDetail` for the wrapper that uses
   *  this to construct a `LabResult` for `LabResultDetailSheet`. */
  sourceResultId?: string;
  /** The `TestResult.date` of the interpreted result. */
  sourceResultDate?: string | null;
  /** Cycle and treatment timing used by the interpretation, when known. */
  interpretationContext?: ResultInterpretationContext;
  /* --------------------------------------------------------------
   * The fields below are legacy copy fields used by
   * `HomepageReviewConceptHome` fixtures and dev previews. The hook
   * does NOT populate them any more — clinical framing lives in the
   * detail sheet and the biomarker factsheet, not here. Kept optional
   * so existing callers compile until they are cleaned up.
   * -------------------------------------------------------------- */
  headline?: string;
  interpretation?: string;
  whatThisMeans?: string;
  nextStep?: string;
};

/**
 * A React key that is unique per interpreted result.
 *
 * `testName` is not one. Serial monitoring is the normal case — oestradiol, LH
 * and AMH are each measured on several days of one stimulation — so a list keyed
 * by the test's name collides on every repeat measurement. React's own warning
 * spells out the consequence: children "may be duplicated and/or omitted", so a
 * result she has recorded can silently vanish from the list rather than fail
 * loudly.
 *
 * `sourceResultId` is the row's own `TestResult.id` and is the stable answer
 * whenever the API supplied it. The fallback carries the date so repeats are
 * distinguishable, and the index last so uniqueness is guaranteed even for two
 * measurements of one test on one day — which the record permits.
 */
export function resultInterpretationKey(
  interpretation: Pick<
    ResultInterpretation,
    "testName" | "sourceResultId" | "sourceResultDate"
  >,
  index: number,
): string {
  if (interpretation.sourceResultId) return interpretation.sourceResultId;
  return `${interpretation.testName}|${interpretation.sourceResultDate ?? ""}|${index}`;
}

export type TestResultInsightsQueryState = {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
};

// -------------------------------------------------------------
// The hook
// -------------------------------------------------------------

/**
 * Interprets test results and returns a `ResultInterpretation` per
 * recognized biomarker. Self-sufficient: the shared category resolver
 * pulls her age and cycle context, so callers don't have to wire them.
 */
export function useTestResultInsights(): {
  interpretations: ResultInterpretation[];
  hasResults: boolean;
  queryState: TestResultInsightsQueryState;
} {
  const testResultsQuery = useTestResults();
  const { data: testResults } = testResultsQuery;
  const categoryResolver = useResultCategoryResolver();
  const t = useTranslations();

  const interpretations = useMemo(() => {
    const results: ResultInterpretation[] = [];
    if (!testResults || !Array.isArray(testResults)) return results;

    for (const result of testResults) {
      const test = getInterpretedTest(result.name);
      if (!test) continue;
      const categorizable = categorizableFromTestResult(result);
      const numericValue = parseFloat(categorizable.value);
      if (isNaN(numericValue)) continue;

      const interpretationContext = categoryResolver.contextFor(categorizable);
      const resolved = categoryResolver.categorize(
        categorizable,
        interpretationContext,
      );
      if (resolved === null) continue;

      const copy = INTERPRETED_TEST_COPY[test];
      results.push({
        testName: t(copy.testNameKey),
        test,
        testPurpose: t(copy.testPurposeKey),
        value: String(numericValue),
        unit: result.unit ?? copy.defaultUnit,
        category: resolved.category,
        categorySource: resolved.source,
        sourceResultId: result.id,
        sourceResultDate: result.date,
        // AMH is cycle-day independent, so it carries no timing context.
        interpretationContext:
          test === "amh" ? undefined : interpretationContext,
      });
    }

    // Semen analysis. Aggregate multi-parameter row.
    const saResults = (testResults as SemenAnalysisRow[]).filter(
      (r): r is SemenAnalysisRow => r.type === "semenFluidAnalysis",
    );
    if (saResults.length > 0) {
      const sa = saResults[0]!;
      const saData = {
        count: parseFloat(String(sa.concentration ?? sa.count ?? "")),
        motility: parseFloat(String(sa.totalMotility ?? sa.motility ?? "")),
        morphology: parseFloat(String(sa.morphology ?? "")),
        volume: parseFloat(String(sa.volume ?? "")),
      };
      const hasAnyField = Object.values(saData).some((v) => !isNaN(v));
      if (hasAnyField) {
        const cleanData: {
          count?: number;
          motility?: number;
          morphology?: number;
          volume?: number;
        } = {};
        for (const [k, v] of Object.entries(saData)) {
          if (!isNaN(v)) {
            (cleanData as Record<string, number>)[k] = v;
          }
        }
        const resolved = resolveSemenAnalysisCategory({
          clinicalAssessment: sa.clinicalAssessment,
          parameters: cleanData,
        });
        results.push({
          testName: t("test_result_insights.sa.test_name"),
          test: "semen_analysis",
          testPurpose: t("test_result_insights.sa.test_purpose"),
          value: t("test_result_insights.sa.value_see_details"),
          valueInDetails: true,
          unit: "",
          category: resolved.category,
          categorySource: resolved.source,
          sourceResultId: sa.id,
          sourceResultDate: sa.date,
        });
      }
    }

    return results;
  }, [testResults, categoryResolver, t]);

  return {
    interpretations,
    hasResults: interpretations.length > 0,
    queryState: {
      // A query with data remains usable while a background refresh runs. Only
      // the initial no-data request should replace the Testing card with a
      // loading state.
      isLoading: testResultsQuery.isLoading,
      isError: testResultsQuery.isError && testResults === undefined,
      isFetching: testResultsQuery.isFetching,
      onRetry: () => {
        void testResultsQuery.refetch();
      },
    },
  };
}
